import { filter as fuzzFilter, score as fuzzScore } from "fuzzaldrin-plus";
import React from "react";

export interface ScoredItem<T> {
  item: T;
  score: number;
}

export function fuzzyFilterAndScore<T>(
  items: readonly T[],
  query: string,
  getText: (item: T) => string
): ScoredItem<T>[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return items.map((item) => ({ item, score: 0 }));
  }

  const tokens = trimmed.split(/\s+/).filter(Boolean);

  const sets = tokens.map((t) => {
    const values = items.map((it) => getText(it));
    const matchedValues = new Set(fuzzFilter(values, t));
    return new Set(items.filter((it) => matchedValues.has(getText(it))));
  });

  const andResult = items.filter((it) => sets.every((s) => s.has(it)));

  const scored = andResult.map((it) => {
    const text = getText(it);
    const tokenScores = tokens.map((t) => fuzzScore(text, t));
    const total = tokenScores.reduce((acc, n) => acc + n, 0);
    return { item: it, score: total };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored;
}

export function fuzzyHighlight(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (!trimmed) {
    return text;
  }

  const tokens = trimmed.split(/\s+/).filter(Boolean);

  const matchedChars = new Set<number>();

  tokens.forEach((token) => {
    const filteredResults = fuzzFilter([text], token);
    if (filteredResults.length > 0) {
      const lowerText = text.toLowerCase();
      const lowerToken = token.toLowerCase();

      let searchIndex = 0;
      for (const char of lowerToken) {
        const charIndex = lowerText.indexOf(char, searchIndex);
        if (charIndex !== -1) {
          matchedChars.add(charIndex);
          searchIndex = charIndex + 1;
        }
      }
    }
  });

  if (matchedChars.size === 0) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  const sortedIndices = Array.from(matchedChars).sort((a, b) => a - b);

  for (const charIndex of sortedIndices) {
    if (charIndex > currentIndex) {
      parts.push(text.slice(currentIndex, charIndex));
    }

    parts.push(
      React.createElement("strong", { key: keyCounter++ }, text[charIndex])
    );
    currentIndex = charIndex + 1;
  }

  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  return React.createElement("span", {}, ...parts);
}
